import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getToCollect } from '../utils/api';
import MoneyIn from './MoneyIn';
import { useNicknames } from '../utils/people';

/* Collection — what has to be picked up, and when. Built on the Money In layout.
   "To collect": every payment on the records that isn't Collected / Waived / N/A, by its due date.
   Past due is always at the top. Today / Tomorrow / Day after / 1 week / 2 weeks / a calendar range.
   A payment with no date is still listed with a PROJECTED date (clearly marked, not the actual
   date): from the job's own remeasure / start / completion date, otherwise the sold date + how
   long that payment usually takes on other jobs.
   "Collected" (admins): what came in — the former Money In report.                         */

const TZ = 'America/Los_Angeles';
const SERVICE_LABELS = { Roof: 'Roofing', EC: 'Exterior Coating', Win: 'Windows', Gut: 'Gutters' };
const PRODUCT_COLORS = { roof: '#C6E0B4', coating: '#ADD8E6', paint: '#C9E4F2', windows: '#FF5050', insulation: '#CC66FF', gutters: '#FFE0B2', patio: '#FFF9C4', fencing: '#F8BBD0' };
const productColor = s => { const t = String(s || '').toLowerCase(); for (const [k, c] of Object.entries(PRODUCT_COLORS)) if (t.includes(k)) return c; return '#E9DDF7'; };

const todayCA = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
const addDays = (iso, n) => { const [y, m, d] = iso.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10); };
const prettyDay = iso => { const [y, m, d] = iso.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); };
const shortDay = iso => { const [y, m, d] = iso.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: Number(y) !== Number(todayCA().slice(0, 4)) ? 'numeric' : undefined }); };
const money = n => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const QUICK = [
  { k: 'today', label: 'Today', range: t => [t, t] },
  { k: 'tomorrow', label: 'Tomorrow', range: t => [addDays(t, 1), addDays(t, 1)] },
  { k: 'after', label: 'Day after', range: t => [addDays(t, 2), addDays(t, 2)] },
  { k: 'week', label: 'Next 7 days', range: t => [t, addDays(t, 6)] },
  { k: '2weeks', label: 'Next 2 weeks', range: t => [t, addDays(t, 13)] },
  { k: 'month', label: 'Next 30 days', range: t => [t, addDays(t, 29)] },
];

const CSS = `
.co-tabs{display:inline-flex;border:1px solid #d6dfdb;border-radius:12px;overflow:hidden;margin-bottom:18px;background:#fff}
.co-tabs button{border:none;background:#fff;padding:10px 20px;font-weight:800;font-size:14px;cursor:pointer;color:#4A5D55}
.co-tabs button.on{background:#0D3B2E;color:#fff}
.mi-bar{display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-bottom:18px}
.co-quick{display:inline-flex;flex-wrap:wrap;gap:6px}
.co-quick button{border:1px solid #d6dfdb;background:#fff;border-radius:999px;padding:7px 14px;font-weight:700;font-size:13px;cursor:pointer;color:#4A5D55}
.co-quick button.on{background:#0D3B2E;color:#fff;border-color:#0D3B2E}
.co-range{display:inline-flex;align-items:center;gap:8px;flex-wrap:wrap}
.co-range input{width:160px}
.mi-tiles{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:20px}
@media(max-width:900px){.mi-tiles{grid-template-columns:repeat(2,1fr)}}
.mi-tile{background:#fff;border:1px solid #E3E9E5;border-radius:14px;padding:16px 18px}
.mi-tile.main{background:linear-gradient(135deg,#0D3B2E,#1B5E40);border:none}
.mi-tile .k{font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#7a8a82}
.mi-tile .v{font-size:24px;font-weight:800;color:#0D3B2E;margin-top:6px}
.mi-tile .s{font-size:12px;color:#7a8a82;margin-top:4px}
.mi-tile.main .k,.mi-tile.main .s{color:rgba(255,255,255,.75)}
.mi-tile.main .v{color:#F0C842;font-size:28px}
.mi-tile.late{border-color:#F5B7B1;background:#FFF6F5}.mi-tile.late .v{color:#B23B2E}
.mi-tile.proj .v{color:#8A5A00}
.mi-sec{margin-bottom:20px}
.mi-sec table{width:100%;border-collapse:collapse}
.mi-sec th{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#7a8a82;text-align:left;padding:10px 12px;border-bottom:1px solid #E3E9E5;background:#F8FAF9}
.mi-sec td{padding:10px 12px;border-bottom:1px solid #EEF2F0;font-size:14px;vertical-align:top}
.mi-sec tr.click{cursor:pointer}
.mi-sec tr.click:hover td{background:#F5F9F7}
.mi-sec td.r,.mi-sec th.r{text-align:right;white-space:nowrap}
.mi-sec tfoot td{font-weight:800;background:#F8FAF9}
.mi-sec .table-wrap{overflow-x:auto}
.mi-empty{padding:18px;color:#7a8a82;font-size:14px}
.mi-tag{display:inline-block;font-size:11px;font-weight:700;padding:2px 8px;border-radius:999px;background:#EEF3F0;color:#4A5D55;margin-left:6px;white-space:nowrap}
.co-late .card-header{background:#FFF1EF}
.co-late .card-header h3{color:#B23B2E}
.co-day td{background:#F4F7F5;font-weight:800;color:#0D3B2E;font-size:13px;padding:8px 12px}
.co-day td span{font-weight:600;color:#7a8a82;margin-left:8px}
.co-proj td{font-style:italic;background:repeating-linear-gradient(135deg,#fff,#fff 8px,#FFFBF0 8px,#FFFBF0 16px)}
.co-proj td.r{font-style:normal}
.co-ptag{display:inline-block;font-style:normal;font-size:11px;font-weight:800;padding:2px 8px;border-radius:6px;border:1.5px dashed #C99A2E;color:#8A5A00;background:#FFF8E6;margin-top:3px}
.co-basis{display:block;font-size:11.5px;color:#8A7A55;margin-top:3px;font-style:italic}
.co-late-tag{display:inline-block;font-size:11px;font-weight:800;padding:2px 8px;border-radius:999px;background:#FDECEA;color:#B23B2E;margin-top:3px}
.co-prod{display:inline-block;padding:1px 8px;border-radius:6px;font-size:12px;font-weight:700;color:#1d2522}
.co-sub{font-size:12px;color:#7a8a82}
.co-note{background:#FFF8E6;border:1px solid #F3D27A;border-radius:12px;padding:12px 16px;font-size:13px;color:#6B5200;margin-bottom:20px}
.co-print{margin-left:auto}
.co-tabs button.warn{color:#B35C00}
.co-tabs button.warn.on{background:#B35C00;color:#fff}
.co-tabs{flex-wrap:wrap}
.co-important{background:#FFF4E5;border:2px solid #E8A33D;border-radius:14px;padding:16px 20px;margin-bottom:18px;color:#5A3A00}
.co-important .tag{display:inline-block;background:#B35C00;color:#fff;font-weight:900;letter-spacing:.12em;font-size:12px;padding:3px 10px;border-radius:6px;margin-bottom:8px}
.co-important h3{margin:0 0 6px;font-size:18px;color:#7A3E00}
.co-important p{margin:0;font-size:14px;line-height:1.55}
@media print{
  .co-tabs,.mi-bar,.co-noprint{display:none!important}
  .mi-tiles{grid-template-columns:repeat(4,1fr)}
  .mi-sec td,.mi-sec th{padding:5px 6px;font-size:11px}
  .co-proj td,.co-day td,.co-prod,.mi-tile.main{-webkit-print-color-adjust:exact;print-color-adjust:exact}
}
@media(max-width:600px){.mi-sec td,.mi-sec th{padding:8px}.mi-tile .v{font-size:20px}.mi-tile.main .v{font-size:22px}.co-hide-sm{display:none}}
`;

function Row({ r, open, showDate }) {
  const nick = useNicknames();
  const prod = r.products[0] || SERVICE_LABELS[r.service] || r.service;
  return (
    <tr className={'click' + (r.projected ? ' co-proj' : '')} onClick={() => open(r)} title="Open this record">
      {showDate && <td style={{ whiteSpace: 'nowrap' }}>{shortDay(r.due)}{r.overdue && <><br /><span className="co-late-tag">{r.projected ? 'past due (estimated)' : `${r.daysLate} day${r.daysLate === 1 ? '' : 's'} late`}</span></>}</td>}
      <td>
        <div style={{ fontWeight: 700, fontStyle: 'normal' }}>{r.customer_name || '—'}</div>
        <div className="co-sub">{r.po_number}{r.sales_rep ? ` · ${nick(r.sales_rep)}` : ''}</div>
      </td>
      <td className="co-hide-sm">
        <div>{[r.address, r.city].filter(Boolean).join(', ') || '—'}</div>
        {r.phone && <div className="co-sub">{r.phone}</div>}
      </td>
      <td>
        <span className="co-prod" style={{ background: productColor(SERVICE_LABELS[r.service] || prod) }}>{SERVICE_LABELS[r.service] || prod}</span>
        <div style={{ marginTop: 3 }}>{r.payment_label}</div>
        {r.projected && <>
          <span className="co-ptag">Projected: no date entered</span>
          <span className="co-basis">Not the actual date. Estimated {r.basis}.</span>
        </>}
      </td>
      <td className="r" style={{ fontWeight: 800, color: '#0D3B2E' }}>{money(r.amount)}</td>
    </tr>
  );
}

function ToCollect({ data, error, onNoDate }) {
  const navigate = useNavigate();
  const [quick, setQuick] = useState('today');
  const [from, setFrom] = useState(todayCA());
  const [to, setTo] = useState(todayCA());

  const today = data?.today || todayCA();
  function pickQuick(q) {
    const def = QUICK.find(x => x.k === q);
    setQuick(q);
    if (def) { const [a, b] = def.range(today); setFrom(a); setTo(b); }
  }
  const lo = from <= to ? from : to, hi = from <= to ? to : from;

  const { late, inRange, byDay } = useMemo(() => {
    const items = (data?.items || []).filter(r => !r.projected);   // only payments with a real date entered
    const late = items.filter(r => r.overdue);
    const inRange = items.filter(r => !r.overdue && r.due >= lo && r.due <= hi);
    const byDay = [];
    for (const r of inRange) { const last = byDay[byDay.length - 1]; if (last && last.date === r.due) last.rows.push(r); else byDay.push({ date: r.due, rows: [r] }); }
    return { late, inRange, byDay };
  }, [data, lo, hi]);

  const open = r => r.po_number && navigate(`/update?po=${encodeURIComponent(r.po_number)}`);
  const sum = list => list.reduce((s, r) => s + r.amount, 0);
  const label = lo === hi ? (lo === today ? `Today · ${prettyDay(lo)}` : prettyDay(lo)) : `${shortDay(lo)} – ${shortDay(hi)}`;
  const dated = (data?.items || []).filter(r => !r.projected);
  const undated = (data?.items || []).filter(r => r.projected);
  const loanTotal = (data?.loans || []).reduce((s, l) => s + l.net, 0);

  if (error) return <div className="co-note" style={{ background: '#FDECEA', borderColor: '#F5B7B1', color: '#8E1B10' }}>{error}</div>;
  if (!data) return <div className="loader"><div className="spinner" /></div>;

  return (
    <>
      <div className="mi-bar">
        <div className="co-quick">
          {QUICK.map(q => <button key={q.k} type="button" className={quick === q.k ? 'on' : ''} onClick={() => pickQuick(q.k)}>{q.label}</button>)}
          <button type="button" className={quick === 'custom' ? 'on' : ''} onClick={() => setQuick('custom')}>Pick dates</button>
        </div>
        {quick === 'custom' && (
          <div className="co-range">
            <input className="form-input" type="date" value={from} onChange={e => e.target.value && setFrom(e.target.value)} aria-label="From" />
            <span style={{ fontWeight: 700, color: '#7a8a82' }}>to</span>
            <input className="form-input" type="date" value={to} onChange={e => e.target.value && setTo(e.target.value)} aria-label="To" />
          </div>
        )}
        <button type="button" className="btn btn-outline btn-sm co-print" onClick={() => window.print()}>🖨 Print list</button>
      </div>

      <div className="mi-tiles">
        <div className="mi-tile main">
          <div className="k">To collect · {lo === hi ? (lo === today ? 'today' : shortDay(lo)) : 'selected days'}</div>
          <div className="v">{money(sum(inRange))}</div>
          <div className="s">{inRange.length} payment{inRange.length === 1 ? '' : 's'} · {label}</div>
        </div>
        <div className="mi-tile late">
          <div className="k">Past due</div>
          <div className="v">{money(sum(late))}</div>
          <div className="s">{late.length} payment{late.length === 1 ? '' : 's'} already late</div>
        </div>
        <div className="mi-tile">
          <div className="k">Still open in total</div>
          <div className="v">{money(sum(dated))}</div>
          <div className="s">{dated.length} payment{dated.length === 1 ? '' : 's'} with a date entered</div>
        </div>
        <button type="button" className="mi-tile proj co-noprint" style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit', borderColor: undated.length ? '#E8B04A' : undefined, background: undated.length ? '#FFF8E6' : undefined }} onClick={onNoDate}>
          <div className="k" style={{ color: undated.length ? '#B35C00' : undefined }}>⚠ No date entered</div>
          <div className="v">{undated.length} · {money(sum(undated))}</div>
          <div className="s">{undated.length ? 'Not in this list. Open the "No date entered" tab →' : 'Every open payment has a date'}</div>
        </button>
      </div>

      <div className="card mi-sec co-late">
        <div className="card-header"><h3>⚠ Past due · {money(sum(late))}</h3></div>
        {late.length === 0 ? <div className="mi-empty">Nothing past due. 🎉</div> : (
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Due</th><th>Customer</th><th className="co-hide-sm">Address / phone</th><th>Payment</th><th className="r">Amount due</th></tr></thead>
              <tbody>{late.map(r => <Row key={r.id} r={r} open={open} showDate />)}</tbody>
              <tfoot><tr><td colSpan={4}>Total past due</td><td className="r">{money(sum(late))}</td></tr></tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="card mi-sec">
        <div className="card-header"><h3>To collect · {label}</h3></div>
        {inRange.length === 0 ? <div className="mi-empty">Nothing due {lo === hi ? 'on this day' : 'in these dates'}.</div> : (
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Customer</th><th className="co-hide-sm">Address / phone</th><th>Payment</th><th className="r">Amount due</th></tr></thead>
              <tbody>
                {byDay.map(g => [
                  ...(lo !== hi ? [<tr key={'d' + g.date} className="co-day"><td colSpan={4}>{prettyDay(g.date)}{g.date === today ? ' · Today' : ''}<span>{g.rows.length} · {money(sum(g.rows))}</span></td></tr>] : []),
                  ...g.rows.map(r => <Row key={r.id} r={r} open={open} />),
                ])}
              </tbody>
              <tfoot><tr><td colSpan={3}>Total to collect</td><td className="r">{money(sum(inRange))}</td></tr></tfoot>
            </table>
          </div>
        )}
      </div>

      {data.loans.length > 0 && (
        <div className="card mi-sec co-noprint">
          <div className="card-header"><h3>Financing waiting to fund · {money(loanTotal)}</h3></div>
          <div style={{ padding: '0 16px 6px', fontSize: 13, color: '#7a8a82' }}>Not picked up by our team. The lender pays these once funded (shown after the dealer fee).</div>
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Customer</th><th>Lender</th><th>Status</th><th className="r">Loan</th><th className="r">We receive</th></tr></thead>
              <tbody>
                {data.loans.map((l, i) => (
                  <tr key={i} className="click" onClick={() => open(l)}>
                    <td><div style={{ fontWeight: 700 }}>{l.customer_name || '—'}</div><div className="co-sub">{l.po_number}{l.city ? ` · ${l.city}` : ''}</div></td>
                    <td>{l.lender_name || '—'}</td>
                    <td><span className="mi-tag">{l.status}</span></td>
                    <td className="r">{money(l.amount)}</td>
                    <td className="r" style={{ fontWeight: 800 }}>{money(l.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

/* Payments still open that have NO date in the record. Kept out of the To collect list on purpose:
   someone needs to open each record and enter the date. The estimate is only a hint.          */
function NoDate({ data, error }) {
  const navigate = useNavigate();
  const nick = useNicknames();
  if (error) return <div className="co-note" style={{ background: '#FDECEA', borderColor: '#F5B7B1', color: '#8E1B10' }}>{error}</div>;
  if (!data) return <div className="loader"><div className="spinner" /></div>;
  const rows = data.items.filter(r => r.projected);
  const open = r => r.po_number && navigate(`/update?po=${encodeURIComponent(r.po_number)}`);
  const total = rows.reduce((s, r) => s + r.amount, 0);
  const jobs = new Set(rows.map(r => r.job_id)).size;
  return (
    <>
      <div className="co-important">
        <div className="tag">IMPORTANT</div>
        <h3>These payments have no date entered. Please check them.</h3>
        <p>{rows.length} open payment{rows.length === 1 ? '' : 's'} on {jobs} job{jobs === 1 ? '' : 's'}, {money(total)} in total, have no date in the record, so they are <b>not</b> in the To collect list and nobody is scheduled to pick them up.
          Open each record (click the row), go to Cash Payments and enter the date the payment is due or was collected. Once a date is entered it moves to the To collect list on its own.</p>
      </div>
      <div className="card mi-sec">
        <div className="card-header"><h3>No date entered · {money(total)}</h3></div>
        {rows.length === 0 ? <div className="mi-empty">Every open payment has a date. Nothing to check. 🎉</div> : (
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Customer</th><th className="co-hide-sm">Address / phone</th><th>Payment</th><th>Rough estimate <span style={{ textTransform: 'none', fontWeight: 500 }}>(not the real date)</span></th><th className="r">Amount</th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="click" onClick={() => open(r)} title="Open this record to enter the date">
                    <td><div style={{ fontWeight: 700 }}>{r.customer_name || '—'}</div><div className="co-sub">{r.po_number}{r.sales_rep ? ` · ${nick(r.sales_rep)}` : ''}</div></td>
                    <td className="co-hide-sm"><div>{[r.address, r.city].filter(Boolean).join(', ') || '—'}</div>{r.phone && <div className="co-sub">{r.phone}</div>}</td>
                    <td>
                      <span className="co-prod" style={{ background: productColor(SERVICE_LABELS[r.service] || r.products[0]) }}>{SERVICE_LABELS[r.service] || r.products[0] || r.service}</span>
                      <div style={{ marginTop: 3 }}>{r.payment_label}{r.status ? <span className="mi-tag">{r.status}</span> : <span className="mi-tag">no status</span>}</div>
                    </td>
                    <td><span className="co-ptag">No date entered</span><span className="co-basis">Around {shortDay(r.due)}, {r.basis}</span></td>
                    <td className="r" style={{ fontWeight: 800, color: '#0D3B2E' }}>{money(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td colSpan={4}>Total with no date</td><td className="r">{money(total)}</td></tr></tfoot>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default function Collection() {
  const { isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab');
  const tab = asked === 'collected' && isAdmin ? 'collected' : asked === 'nodate' ? 'nodate' : 'due';
  const setTab = t => setParams(t === 'due' ? {} : { tab: t }, { replace: true });
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { getToCollect().then(setData).catch(e => setError(e.response?.data?.error || 'Could not load the collection list')); }, []);
  const noDate = (data?.items || []).filter(r => r.projected).length;

  return (
    <>
      <style>{CSS}</style>
      <div className="page-header">
        <h2>Collection</h2>
        <div style={{ fontSize: 13, color: '#888' }}>
          {tab === 'due' ? 'What has to be picked up and when, straight from the records. Past due first. Only payments with a date entered. California time.'
            : tab === 'nodate' ? 'Open payments with no date in the record. Enter the dates so they show up in To collect.'
            : 'Every payment received: cash collected and financing funded, by the date it came in.'}
        </div>
      </div>
      <div className="page-body">
        <div className="co-tabs">
          <button type="button" className={tab === 'due' ? 'on' : ''} onClick={() => setTab('due')}>To collect</button>
          <button type="button" className={(tab === 'nodate' ? 'on' : '') + (noDate ? ' warn' : '')} onClick={() => setTab('nodate')}>⚠ No date entered{data ? ` (${noDate})` : ''}</button>
          {isAdmin && <button type="button" className={tab === 'collected' ? 'on' : ''} onClick={() => setTab('collected')}>Collected (money in)</button>}
        </div>
        {tab === 'due' ? <ToCollect data={data} error={error} onNoDate={() => setTab('nodate')} />
          : tab === 'nodate' ? <NoDate data={data} error={error} />
          : <MoneyIn embedded />}
      </div>
    </>
  );
}
